import { gConsts } from '@/globals/gConsts';
import { utils } from '@/globals/utils';
import { nodes } from '@/globals/nodes';

import ReportNode from '@/features/report/models/ReportNode';
import LabelAnnotation from '@/features/report/models/LabelAnnotation';
import AnnotationDetailsTextBuilder from '@/features/report/lib/builders/AnnotationDetailsTextBuilder';
import LabelAnnotationValidator from '@/features/report/lib/validators/LabelAnnotationValidator';
import LabelAnnotationSorter from '@/features/report/lib/LabelAnnotationSorter';
import { ESPECIFICACAO_REPORT_MAPPER } from '@/features/report/lib/resources/mappers/EspecificacaoReportMapper';
import { anotacaoUtils } from '@/features/anotacoes/lib/AnotacaoUtils';

function findMatchingSpanClose(html, contentStart) {
    let depth = 1;
    let i = contentStart;
    while (i < html.length && depth > 0) {
        const nextOpen = html.indexOf('<span', i);
        const nextClose = html.indexOf('</span>', i);
        if (nextClose === -1)
            return -1;
        if (nextOpen !== -1 && nextOpen < nextClose) {
            depth++;
            i = nextOpen + 5;
        } else {
            depth--;
            if (depth === 0)
                return nextClose;
            i = nextClose + 7;
        }
    }
    return -1;
}

function replaceLabelAnnotationsDisplaySpan(html, labelId, replacement) {
    const escapedLabelId = labelId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const spanMatcher = `<span\\s(?=[^>]*\\bdata-type="label-annotations-display")(?=[^>]*\\blabelid="${escapedLabelId}")[^>]*>`;
    const selfClosingRegex = new RegExp(`${spanMatcher}/>`, 'g');
    const openTagRegex = new RegExp(spanMatcher, 'g');

    html = html.replace(selfClosingRegex, replacement);

    let result = '';
    let lastIndex = 0;
    let match;

    while ((match = openTagRegex.exec(html)) !== null) {
        const openTagStart = match.index;
        const contentStart = openTagStart + match[0].length;
        const closeTagStart = findMatchingSpanClose(html, contentStart);
        if (closeTagStart === -1)
            break;

        result += html.slice(lastIndex, openTagStart);
        result += replacement;
        lastIndex = closeTagStart + 7;
    }

    result += html.slice(lastIndex);
    return result;
}

export const reportUtils = {
    getNodeReportTextById(nodeId) {
        const node = nodes.getNode(nodeId);
        if (!node)
            return '';
        return this.getNodeReportText(node);
    },
    getNodeReportText(node) {
        if (!node)
            return '';
        const labelInfo = anotacaoUtils.getLabelInfoByLabelId(node.id);
        if (!labelInfo) {
            if (node.report.type == gConsts.node.report.types.NONE)
                return node.layout.caption;
            return node.report.text;
        }
        if (labelInfo.annotation.type == gConsts.node.report.annotation.types.LOCATION)
            return labelInfo.layout.caption;
        return labelInfo.report.text;
    },
    nodeHasReportLine(node) {
        if (!node || node.layout.type != gConsts.node.layout.types.LABEL)
            return false;
        return ![gConsts.node.report.types.NONE, gConsts.node.report.types.SELECT_ITEM, gConsts.node.report.types.NODE_LIST_ITEM].includes(node.report.type);
    },
    labelSearchForReportMatches(labelNode, searchText) {
        if (!this.nodeHasReportLine(labelNode))
            return false;

        return utils.searchMatches(labelNode.layout.caption, searchText);
    },
    getAnnotationDetailsText(annotation) {
        const annotationDetailsTextBuilder = new AnnotationDetailsTextBuilder(annotation);
        return annotationDetailsTextBuilder.build();
    },
    /**
     * @param {LabelAnnotation} annotation 
     * @returns {string}
     */
    getAnnotationItemElementText(annotation) {
        const color = annotation.color;

        const annotationDetailsText = this.getAnnotationDetailsText(annotation);

        return `<span data-type="report-label-annotation-item" data-annotation-id="${annotation.id}" style="--annotation-color: ${color}">
        <span class="text">${annotation.reportText}</span>
        ${annotationDetailsText.length > 0 ? `<span class="details">${annotationDetailsText}</span>` : ''}
        </span>`;
    },
    /**
     * @param {LabelAnnotation[]} annotations 
     * @returns {string}
     */
    getAnnotationsText(annotations) {
        let text = '';

        for (let index = 0; index < annotations.length; index++) {
            const annotation = annotations[index];
            text += this.getAnnotationItemElementText(annotation);
            if (index < annotations.length - 1) {
                text += '<span class="separator">, </span>';
            }
        }

        if (annotations.length == 0) {
            text += `<span data-type="report-label-annotation-item" data-annotation-id="0">
            <span class="text">Selecione na imagem</span>
            </span>`;
        }

        return text;
        // if (locations.some(location => location.id.startsWith('lb_dente'))) {
        //     return this.getListReportText(locations.map(location => location.text)) || '';
        // }
        // return locations.map(location => location.text).join('; ').trim() || '';
    },
    getInitialReportText(node, annotations = []) {
        if (!node)
            return '';
        const reportText = this.getNodeReportText(node) || node?.layout?.caption;
        // Por enquanto texto simples para nodes que não são labels
        if (node.layout.type != gConsts.node.layout.types.LABEL)
            return reportText;
        if (!anotacaoUtils.labelHasAnnotations(node.id)) {
            return `<p>${reportText}</p>`;
        }
        const associationType = anotacaoUtils.getAssociationTypeByLabelId(node?.id);
        let annotationList = annotations;
        if (associationType == gConsts.node.report.annotation.association.GROUP && annotations.length > 0) {
            annotationList = [annotations[0]];
        }
        annotationList = annotationList
            .filter(annotation => LabelAnnotationValidator.run(annotation))
            .sort(LabelAnnotationSorter.run);
        const annotationsText = this.getAnnotationsText(annotationList);
        return `<p>${reportText} <span labelid="${node.id}" annotationstext="${JSON.stringify(annotationList).replace(/"/g, '&quot;')}" data-type="label-annotations-display">${annotationsText}</span></p>`
    },
    /**
     * 
     * @param {ReportNode} reportNode 
     * @returns 
     */
    getReportNodeAnnotations(reportNode) {
        if (!reportNode || !reportNode.annotation?.list)
            return [];

        const associationType = anotacaoUtils.getAssociationTypeByAnnotationType(reportNode.annotation.type);

        let annotationList = reportNode.annotation.list;
        if (associationType == gConsts.node.report.annotation.association.GROUP && annotationList.length > 0) {
            annotationList = [annotationList[0]];
        }

        return annotationList
            .filter(annotation => LabelAnnotationValidator.run(annotation))
            .sort(LabelAnnotationSorter.run);
    },
    /**
     * @param {ReportNode} labelReportNode 
     */
    refreshLabelAnnotationsReportText(labelReportNode) {
        if (!labelReportNode)
            return;
        // console.log('refreshLabelReportTextLocations: labelReportNode: ', JSON.stringify(labelReportNode));
        let updatedReportText = labelReportNode.reportText;
        const labelAnnotations = this.getReportNodeAnnotations(labelReportNode);
        const replacementSpan = `<span labelid="${labelReportNode.id}" annotationstext="${JSON.stringify(labelAnnotations).replace(/"/g, '&quot;')}" data-type="label-annotations-display">${this.getAnnotationsText(labelAnnotations)}</span>`;
        updatedReportText = replaceLabelAnnotationsDisplaySpan(updatedReportText, labelReportNode.id, replacementSpan);
        labelReportNode.reportText = updatedReportText;
        // console.log('refreshLabelReportTextLocations: updatedReportText: ', updatedReportText);
    },

    /**
     * @description Retorna o texto de uma lista de itens separados por vírgula, com exceção do último item que é separado por "e"
     * @param {string[]} list 
     * @returns {string}
     */
    getListReportText(list) {
        if (!list || list.length == 0)
            return '';

        if (list.length == 1) {
            return list[0] || '';
        } else {
            return list.reduce((text, item, index) => {
                if (index == 0)
                    return text + item;
                if (index == list.length - 1)
                    return text + ' e ' + item;
                return text + ', ' + item;
            }, '');
        }
    },

    getEspecificacaoReport(especificacao) {
        const especificacaoValue = especificacao?.split('__')?.[1];
        if (!especificacaoValue)
            return null;
        return ESPECIFICACAO_REPORT_MAPPER[especificacaoValue] || null;
    }
}